import { graphQLErrorsOf, networkErrorOf } from './apolloErrors';

export function getGraphQLErrorMessages(err: unknown): string[] {
  const network = networkErrorOf(err);
  if (network) {
    return [`Network Error: ${network.message}`];
  }

  return graphQLErrorsOf(err).map(({ message }) => message);
}

/**
 * Parses GraphQL errors and returns user-friendly messages for common scenarios
 */
export function parseGraphQLErrorForUser(err: unknown): string {
  if (networkErrorOf(err)) {
    return 'Network connection error. Please check your internet connection and try again.';
  }

  const graphQLErrors = graphQLErrorsOf(err);
  if (graphQLErrors.length === 0) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Get the first error message (most relevant)
  const errorMessage = graphQLErrors[0].message;

  // Handle common error patterns with user-friendly messages

  // MongoDB E11000 duplicate key errors — extract the offending field from the index name
  if (errorMessage.includes('E11000') || errorMessage.includes('duplicate key')) {
    const indexMatch = errorMessage.match(/index:\s+([^\s]+)/);
    const indexName = indexMatch ? indexMatch[1] : '';
    const fields = indexName
      .split('_')
      .filter((part) => part && part !== '1' && part !== '-1' && part !== 'tenantId');
    const fieldLabel = fields
      .map((field) => field.replace(/([A-Z])/g, ' $1').toLowerCase().trim())
      .join(', ');
    if (fieldLabel) {
      return `Another client already uses this ${fieldLabel}. Please choose a different value.`;
    }
    return 'A record with these values already exists. Please choose different values.';
  }

  // Duplicate key/unique constraint violations
  if (errorMessage.includes('already exists')) {
    if (errorMessage.includes('Feature flag')) {
      const keyMatch = errorMessage.match(/with key "([^"]+)"/);
      const key = keyMatch ? keyMatch[1] : 'this key';
      return `A feature flag with the key "${key}" already exists. Please choose a different key.`;
    }
    if (errorMessage.includes('Category')) {
      return 'A category with this name already exists. Please choose a different name.';
    }
    if (errorMessage.includes('Module')) {
      return 'A module with this name already exists. Please choose a different name.';
    }
    if (errorMessage.includes('Bundle')) {
      return 'A bundle with this name already exists. Please choose a different name.';
    }
    if (errorMessage.includes('Organization')) {
      return 'A client with this name already exists. Please choose a different name.';
    }
    return 'This item already exists. Please choose a different name or key.';
  }

  // Validation errors
  if (
    errorMessage.includes('is required') ||
    errorMessage.includes('cannot be empty')
  ) {
    return 'Please fill in all required fields.';
  }

  if (
    errorMessage.includes('invalid format') ||
    errorMessage.includes('format is invalid')
  ) {
    return 'Please check the format of your input and try again.';
  }

  // Permission/authorization errors
  if (
    errorMessage.includes('not authorized') ||
    errorMessage.includes('permission denied')
  ) {
    return 'You do not have permission to perform this action.';
  }

  if (
    errorMessage.includes('access denied') ||
    errorMessage.includes('forbidden')
  ) {
    return 'Access denied. Please contact your administrator.';
  }

  // Resource not found
  if (
    errorMessage.includes('not found') ||
    errorMessage.includes('does not exist')
  ) {
    return 'The requested item could not be found. It may have been deleted or moved.';
  }

  // Rate limiting
  if (
    errorMessage.includes('rate limit') ||
    errorMessage.includes('too many requests')
  ) {
    return 'Too many requests. Please wait a moment and try again.';
  }

  // Validation length errors
  if (
    errorMessage.includes('too long') ||
    errorMessage.includes('exceeds maximum length')
  ) {
    return 'One or more fields are too long. Please shorten your input.';
  }

  if (
    errorMessage.includes('too short') ||
    errorMessage.includes('minimum length')
  ) {
    return 'One or more fields are too short. Please provide more details.';
  }

  // Dependency/relationship errors
  if (
    errorMessage.includes('in use') ||
    errorMessage.includes('referenced by')
  ) {
    return 'This item cannot be deleted because it is being used elsewhere.';
  }

  // Default fallback - return the original message if it's user-friendly enough
  // (not too technical and not too long)
  if (
    errorMessage.length < 150 &&
    !errorMessage.includes('GraphQL') &&
    !errorMessage.includes('SQL')
  ) {
    return errorMessage;
  }

  // Final fallback for technical errors
  return 'An error occurred while processing your request. Please try again or contact support if the problem persists.';
}

export default function normalizeGraphqlError(err: unknown): string {
  return getGraphQLErrorMessages(err).join('. ');
}
